import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { GameName, STORAGE_KEYS } from './constants.ts';
import Gotcha from './games/Gotcha/Gotcha.tsx';
import GuessThePerson from './games/GuessThePerson.tsx';
import GenderEqualityCuisine from './games/GenderEqualityCuisine/GenderEqualityCuisine.tsx';
import api from './utils/api.ts';
import './games/Game.css';

interface RoomData {
  gameType: string;
  roomName: string;
  roomId: string;
  adminAlias: string;
  adminUserId: string;
  currentParticipantCount: number;
  maxParticipants: number;
  gameState: string;
}

function GameRoom() {
  const { roomId } = useParams<{ roomId: string }>();
  const [roomData, setRoomData] = useState<RoomData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [startingGame, setStartingGame] = useState(false);

  const [hasToken, setHasToken] = useState(!!localStorage.getItem(STORAGE_KEYS.JWT_TOKEN));
  const [guestAlias, setGuestAlias] = useState('');
  const [guestSubmitting, setGuestSubmitting] = useState(false);
  const [guestError, setGuestError] = useState('');
  const [joinedRoom, setJoinedRoom] = useState(false);

  const fetchRoom = async () => {
    if (!roomId) {
      setError('Invalid room ID');
      setLoading(false);
      return;
    }

    const result = await api.getRoom(roomId);

    if (result.success && result.data.exists) {
      setRoomData(result.data.room);
    } else {
      setError('Room not found');
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchRoom();
  }, [roomId]);

  // Once we have a token + room data, join the room as a participant (idempotent on backend).
  useEffect(() => {
    const join = async () => {
      if (!hasToken || !roomData || !roomId || joinedRoom) return;
      const userData = localStorage.getItem(STORAGE_KEYS.USER_DATA);
      const username = localStorage.getItem(STORAGE_KEYS.USERNAME);
      if (!userData) return;
      const alias = username || JSON.parse(userData).userAlias || 'Player';
      const result = await api.joinGame(roomId, alias);
      if (result.success) {
        setJoinedRoom(true);
        fetchRoom();
      }
    };
    join();
  }, [hasToken, roomData, roomId, joinedRoom]);

  const handleStartGame = async () => {
    if (!roomId) return;

    setStartingGame(true);
    const result = await api.startGame(roomId);

    if (result.success) {
      await fetchRoom();
    } else {
      setError(result.error || 'Failed to start game');
    }
    setStartingGame(false);
  };

  const handleGuestLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setGuestError('');
    const trimmed = guestAlias.trim();
    if (trimmed.length < 2) {
      setGuestError('Please enter at least 2 characters');
      return;
    }
    setGuestSubmitting(true);
    const result = await api.guestLogin(trimmed);
    setGuestSubmitting(false);
    if (result.success) {
      setHasToken(true);
    } else {
      setGuestError(result.error || 'Failed to join as guest');
    }
  };

  const isAdmin = () => {
    const userData = localStorage.getItem(STORAGE_KEYS.USER_DATA);
    if (!userData || !roomData) return false;
    const user = JSON.parse(userData);
    const userId = user.userId || user.user_id;
    return userId === roomData.adminUserId;
  };

  if (loading) {
    return <div className="loading">🔄 Loading game room...</div>;
  }

  if (error) {
    return <div className="error">⚠️ {error}</div>;
  }

  if (!hasToken) {
    return (
      <div className="game-container">
        <div className="game-header">
          <h2>🚪 Join Room</h2>
          <div className="room-id-display">{roomId}</div>
          {roomData?.roomName && (
            <div className="game-info">
              <div className="info-item">
                <div className="info-label">Room Name</div>
                <div className="info-value">{roomData.roomName}</div>
              </div>
            </div>
          )}
        </div>
        <div className="game-content">
          <form onSubmit={handleGuestLogin} style={{ maxWidth: 420, margin: '0 auto' }}>
            <h3 style={{ textAlign: 'center', marginBottom: '1rem' }}>Join as Guest</h3>
            <p style={{ textAlign: 'center', color: 'var(--text-secondary)', marginBottom: '1.5rem' }}>
              Pick a name to use in this room. No account needed.
            </p>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 600 }}>
              Your Name
            </label>
            <input
              type="text"
              value={guestAlias}
              onChange={(e) => setGuestAlias(e.target.value)}
              placeholder="e.g. Anna"
              maxLength={20}
              autoFocus
              style={{
                width: '100%',
                padding: '0.85rem',
                fontSize: '1rem',
                borderRadius: 8,
                border: '2px solid var(--secondary)',
                marginBottom: '1rem',
              }}
            />
            {guestError && (
              <div style={{ color: 'var(--error)', marginBottom: '1rem' }}>{guestError}</div>
            )}
            <button
              type="submit"
              disabled={guestSubmitting}
              style={{
                width: '100%',
                padding: '1rem',
                fontSize: '1.05rem',
                fontWeight: 600,
                backgroundColor: 'var(--primary)',
                color: 'white',
                border: 'none',
                borderRadius: 8,
                cursor: guestSubmitting ? 'not-allowed' : 'pointer',
                opacity: guestSubmitting ? 0.6 : 1,
              }}
            >
              {guestSubmitting ? 'Joining…' : 'Join Room'}
            </button>
            <div style={{ textAlign: 'center', marginTop: '1rem' }}>
              <a href="/login" style={{ color: 'var(--text-secondary)', fontSize: '0.9rem' }}>
                Have an account? Log in instead
              </a>
            </div>
          </form>
        </div>
      </div>
    );
  }

  return (
    <>
      {isAdmin() && roomData?.gameState === 'preparation' && (
        <div style={{ padding: '20px', textAlign: 'center' }}>
          <button
            onClick={handleStartGame}
            disabled={startingGame}
            style={{
              padding: '12px 24px',
              fontSize: '16px',
              fontWeight: 'bold',
              backgroundColor: '#4CAF50',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              cursor: startingGame ? 'not-allowed' : 'pointer',
              opacity: startingGame ? 0.6 : 1
            }}
          >
            {startingGame ? 'Starting...' : 'Start Game'}
          </button>
        </div>
      )}
      {roomData?.gameType === GameName.Gotcha && (
        <Gotcha roomId={roomId!} roomData={roomData} />
      )}
      {roomData?.gameType === GameName.GuessThePerson && (
        <GuessThePerson roomId={roomId!} roomData={roomData} />
      )}
      {roomData?.gameType === GameName.GenderEqualityCuisine && (
        <GenderEqualityCuisine roomId={roomId!} roomData={roomData} isAdmin={isAdmin()} />
      )}
    </>
  );
}

export default GameRoom;
